import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSyncedTheme } from '../hooks/useSyncedTheme';
import DashboardHeader from '../components/common/DashboardHeader';
import DashboardMetricCard from '../components/common/DashboardMetricCard';
import DashboardChartCard from '../components/common/DashboardChartCard';
import DashboardReportHeader from '../components/common/DashboardReportHeader';
import { Box, Grid, Alert } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HotelIcon from '@mui/icons-material/Hotel';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getBookingStatistics } from '../services/api';
import { getUserPermissionCodes, hasFeaturePermission } from '../utils/featurePermissions';
import useMobileView from '../hooks/useMobileView';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import {
  getInsightsChartPrimaryGridProps,
  getInsightsChartSecondaryGridProps,
  getInsightsGridSpacing,
  getInsightsMetricGridProps,
} from '../utils/insightsDashboardLayout';

const ROOMNEXA_LOGO_URL = '/static/topbar/logo_ed_dark.png';

const loadRoomnexaLogoDataUrl = async () => {
  try {
    const response = await fetch(ROOMNEXA_LOGO_URL);
    if (!response.ok) return null;
    const logoBlob = await response.blob();
    const objectUrl = URL.createObjectURL(logoBlob);
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = objectUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || 300;
      canvas.height = image.naturalHeight || 87;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    return null;
  }
};

const BookingDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const reportRef = useRef(null);
  const exportInProgressRef = useRef(false);
  const currentTheme = useSyncedTheme();
  const { isCompact } = useMobileView();

  const navLinks = useMemo(() => {
    const permissions = getUserPermissionCodes();
    const can = (code) => hasFeaturePermission(code, permissions);
    const links = [];

    if (can('common.manage_finance')) {
      links.push({ url: '/finance/payments/', label: 'Payments' });
    }
    if (can('common.manage_customer_management')) {
      links.push({ url: '/customerhub/customers/', label: 'Customers' });
    }
    if (can('common.manage_finance') || can('common.view_reports')) {
      links.push({ url: '/finance/reports/', label: 'Reports' });
    }
    if (can('common.manage_finance')) {
      links.push({ url: '/finance/rates/', label: 'Manage Rate' });
    }

    return links;
  }, []);

  // Date range state - default to the full current month
  const [startDate, setStartDate] = useState(moment().startOf('month'));
  const [endDate, setEndDate] = useState(moment().endOf('month'));

  // Fetch booking statistics when date range changes
  useEffect(() => {
    if (startDate && endDate && startDate.isValid() && endDate.isValid()) {
      fetchStatistics();
    }
  }, [startDate, endDate]);

  const fetchStatistics = async () => {
    if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) {
      return;
    }
    
    // Ensure start date is before or equal to end date
    if (startDate.isAfter(endDate)) {
      setError('Start date must be before or equal to end date.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await getBookingStatistics(
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      );
      setStatistics(data);
    } catch (err) {
      setError(err.message || 'Failed to load booking statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (newDate) => {
    if (newDate && moment.isMoment(newDate) && newDate.isValid()) {
      setStartDate(newDate);
    }
  };

  const handleEndDateChange = (newDate) => {
    if (newDate && moment.isMoment(newDate) && newDate.isValid()) {
      setEndDate(newDate);
    }
  };

  const handleDateRangeChange = (nextStartDate, nextEndDate) => {
    if (nextStartDate?.isValid?.() && nextEndDate?.isValid?.()) {
      setStartDate(nextStartDate);
      setEndDate(nextEndDate);
    }
  };

  // Theme-aware colors
  const getThemeColors = () => {
    if (currentTheme === 'dark') {
      return {
        revenue: '#ff6b7d',
        expenses: '#f8e082',
        profit: '#66d4a8',
        pending: '#8ac9ff',
        booking: '#4ce1b6',
        cancelled: '#ff4861',
        active: '#66d4a8',
        rooms: '#8ac9ff',
      };
    } else {
      return {
        revenue: '#ff4861',
        expenses: '#f6da6e',
        profit: '#4ce1b6',
        pending: '#70bbfd',
        booking: '#4ce1b6',
        cancelled: '#ff4861',
        active: '#4ce1b6',
        rooms: '#70bbfd',
      };
    }
  };

  const themeColors = getThemeColors();
  const unifiedChartColors = currentTheme === 'dark'
    ? {
        red: '#ff6b7d',
        green: '#4ce1b6',
        yellow: '#f8e082',
      }
    : {
        red: '#ff4861',
        green: '#4ce1b6',
        yellow: '#f6da6e',
      };
  const bookingSourcePalette = [
    '#4ce1b6',
    '#f6da6e',
    '#70bbfd',
    '#ff4861',
    '#8e7dff',
    '#ff9f43',
    '#26c6da',
    '#66bb6a',
  ];
  const bookingSourceColorMap = {
    Direct: '#4ce1b6',
    Reservation: '#f6da6e',
    'Booking.com': '#2563eb',
    Agoda: '#8e24aa',
    MakeMyTrip: '#ef4444',
    MMT: '#ef4444',
    WalkIn: '#70bbfd',
    'Walk-In': '#70bbfd',
  };

  // Calculate totals from real data if available
  const totalBookings = statistics?.total_bookings || 0;
  const cancelledBookings = statistics?.cancelled_bookings || 0;
  const activeBookings = statistics?.active_bookings || 0;
  const totalRoomBookings = statistics?.total_room_bookings || 0;
  const activeRoomBookings = statistics?.active_room_bookings || 0;
  const cancelledRoomBookings = statistics?.cancelled_room_bookings || 0;

  // Daily statistics for charts: include every date in the selected range, even if counts are zero.
  const dailyStats = statistics?.daily_stats || [];
  const dailyStatsByDate = dailyStats.reduce((acc, stat) => {
    if (stat?.date) {
      acc[stat.date] = stat;
    }
    return acc;
  }, {});

  const getSelectedRangeDailyData = () => {
    if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) {
      return [];
    }

    const allDates = [];
    const currentDate = startDate.clone().startOf('day');
    const lastDate = endDate.clone().startOf('day');

    while (currentDate.isSameOrBefore(lastDate)) {
      const dateKey = currentDate.format('YYYY-MM-DD');
      const stat = dailyStatsByDate[dateKey] || {};

      allDates.push({
        date: dateKey,
        name: currentDate.format('MMM DD'),
        bookings: stat.total_bookings || 0,
        active: stat.active_bookings || 0,
        cancelled: stat.cancelled_bookings || 0,
        booking: Math.max(
          (stat.total_bookings || 0) -
            (stat.active_bookings || 0) -
            (stat.cancelled_bookings || 0),
          0
        ),
        total_room_bookings: stat.total_room_bookings || 0,
        active_room_bookings: stat.active_room_bookings || 0,
        cancelled_room_bookings: stat.cancelled_room_bookings || 0,
        direct: stat.direct_bookings || 0,
        ota: stat.ota_bookings || 0,
      });

      currentDate.add(1, 'day');
    }

    return allDates;
  };

  const bookingStatisticsData = getSelectedRangeDailyData();

  // Room booking status pie chart data (Booking vs Cancel)
  const roomBookingStatusData = [
    {
      name: `Booking (${activeRoomBookings})`,
      value: activeRoomBookings,
      color: themeColors.active,
    },
    {
      name: `Cancel (${cancelledRoomBookings})`,
      value: cancelledRoomBookings,
      color: themeColors.cancelled,
    },
  ].filter(item => item.value > 0);

  // Channel statistics (Direct vs OTA)
  const channelStats = statistics?.channel_statistics || { direct_bookings: 0, ota_bookings: 0 };
  const directBookings = channelStats.direct_bookings || 0;
  const otaBookings = channelStats.ota_bookings || 0;
  const bookingSourceChartData = (statistics?.booking_source_breakdown || [])
    .map((entry, idx) => {
      const name = String(entry?.source || entry?.name || entry?.channel || '').trim();
      return {
        name,
        value: Number(entry?.value) || 0,
        color: bookingSourceColorMap[name] || bookingSourcePalette[idx % bookingSourcePalette.length],
      };
    })
    .filter((item) => item.name && item.value > 0)
    .sort((a, b) => b.value - a.value);
  const totalSourceBookings = bookingSourceChartData.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const cancelledBookingSourceChartData = (statistics?.cancelled_booking_source_breakdown || [])
    .map((entry, idx) => {
      const name = String(entry?.source || entry?.name || entry?.channel || '').trim();
      return {
        name,
        value: Number(entry?.value) || 0,
        color: themeColors.cancelled,
      };
    })
    .filter((item) => item.name && item.value > 0)
    .sort((a, b) => b.value - a.value);
  const totalCancelledSourceBookings = cancelledBookingSourceChartData.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const cancelledRevenueLossBySourceData = (statistics?.cancelled_booking_source_revenue_loss || [])
    .map((entry) => {
      const name = String(entry?.source || entry?.name || entry?.channel || '').trim();
      return {
        name,
        value: Number(entry?.value) || 0,
        color: themeColors.cancelled,
      };
    })
    .filter((item) => item.name && item.value > 0)
    .sort((a, b) => b.value - a.value);
  const totalCancelledRevenueLoss = cancelledRevenueLossBySourceData.reduce((sum, item) => sum + Number(item.value || 0), 0);
  
  // Prepare daily data for Direct vs OTA chart
  const channelData = bookingStatisticsData.map(stat => ({
    name: stat.name,
    direct: stat.direct,
    ota: stat.ota,
  }));

  // Early check-in and late checkout data
  const earlyCheckin = statistics?.early_checkin || { total: 0, bookings: [], daily: {} };
  const lateCheckout = statistics?.late_checkout || { total: 0, bookings: [], daily: {} };
  
  // Prepare daily data for early check-in and late checkout chart
  const checkinCheckoutData = bookingStatisticsData.map(stat => {
    const dateKey = stat.date;
    return {
      name: stat.name,
      early_checkin: earlyCheckin.daily?.[dateKey] || 0,
      late_checkout: lateCheckout.daily?.[dateKey] || 0,
    };
  });


  const isDark = currentTheme === 'dark';
  const formatNumber = (val) => Number(val || 0).toLocaleString('en-IN');
  const formatPdfAmount = (val) =>
    Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const handleDownloadPdf = async () => {
    if (!statistics || loading || exportInProgressRef.current || !reportRef.current) return;
    exportInProgressRef.current = true;
    document.body.classList.add('dashboard-pdf-exporting');
    setPdfExporting(true);
    try {
      const filename = `booking-dashboard-${startDate.format('YYYY-MM-DD')}-${endDate.format('YYYY-MM-DD')}.pdf`;
      const rangeLabel = `${startDate.format('DD MMM YYYY')} to ${endDate.format('DD MMM YYYY')}`;

      await new Promise((resolve) => setTimeout(resolve, 120));
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
      const logoDataUrl = await loadRoomnexaLogoDataUrl();
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: '#ffffff',
        scale: Math.min(2, window.devicePixelRatio || 1),
        useCORS: true,
        allowTaint: true,
        logging: false,
        windowWidth: reportRef.current.scrollWidth,
        windowHeight: reportRef.current.scrollHeight,
        onclone: (_documentClone, clonedElement) => {
          clonedElement.style.backgroundColor = '#ffffff';
        },
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 28;
      const footerHeight = 13;
      const imageTop = headerHeight + margin;
      const printableHeight = pageHeight - imageTop - footerHeight - margin;
      const maxImageWidth = pageWidth - margin * 2;
      let pageIndex = 1;

      const drawPdfHeader = (pageNumber) => {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, headerHeight, 'F');
        if (logoDataUrl) {
          pdf.addImage(logoDataUrl, 'PNG', (pageWidth - 36) / 2, 3.4, 36, 10);
        } else {
          pdf.setTextColor(30, 41, 59);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(18);
          pdf.text('RoomNexa', pageWidth / 2, 8.5, { align: 'center' });
        }
        pdf.setTextColor(30, 41, 59);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Booking Dashboard Report', pageWidth / 2, 18.6, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(100, 116, 139);
        pdf.text(
          `Range: ${rangeLabel} | Generated: ${moment().format('DD MMM YYYY, hh:mm A')} | Page ${pageNumber}`,
          pageWidth / 2,
          23.6,
          { align: 'center' }
        );
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);
      };

      const drawPdfFooter = (pageNumber) => {
        const footerTop = pageHeight - footerHeight;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, footerTop, pageWidth, footerHeight, 'F');
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, footerTop, pageWidth - margin, footerTop);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Powered by RoomNexa', margin, footerTop + 5.2);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6.8);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Professional hotel booking intelligence for smarter operations.', margin, footerTop + 9.2);
        pdf.text(`Page ${pageNumber}`, pageWidth - margin, footerTop + 7.4, { align: 'right' });
      };

      const imageData = canvas.toDataURL('image/png');
      const imageWidth = maxImageWidth;
      const imageHeight = (canvas.height * imageWidth) / canvas.width;
      const imageLeft = (pageWidth - imageWidth) / 2;
      let renderedHeight = 0;
      while (renderedHeight < imageHeight) {
        drawPdfHeader(pageIndex);
        pdf.addImage(imageData, 'PNG', imageLeft, imageTop - renderedHeight, imageWidth, imageHeight);
        drawPdfFooter(pageIndex);
        renderedHeight += printableHeight;
        if (renderedHeight < imageHeight) {
          pdf.addPage();
          pageIndex += 1;
        }
      }

      const formatPaymentBreakdown = (total, methods = []) => {
        const validMethods = (methods || []).filter((item) => Number(item?.value || 0) > 0);
        const totalLabel = formatPdfAmount(total || 0);
        if (!validMethods.length) return totalLabel;
        const detail = validMethods
          .map((item) => `${item.method || item.name || '-'} - ${formatPdfAmount(item.value)}`)
          .join('\n');
        return `${totalLabel}\n${detail}`;
      };
      const reportTableRows = (statistics?.daily_stats || []).map((stat) => [
        moment(stat.date).format('DD MMM YYYY'),
        Number(stat.total_bookings || 0).toString(),
        Number(stat.cancelled_bookings || 0).toString(),
        formatPaymentBreakdown(stat.booking_amount, stat.booking_payment_methods),
      ]);

      if (reportTableRows.length) {
        pdf.addPage();
        pageIndex += 1;
        drawPdfHeader(pageIndex);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Daily Booking Details', margin, imageTop);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7.5);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Date-wise booking count, cancellations, and non-cancelled booking collections.', margin, imageTop + 5);
        drawPdfFooter(pageIndex);

        autoTable(pdf, {
          startY: imageTop + 10,
          head: [['Date', 'Total Booking', 'Canceled Booking', 'Booking Amount']],
          body: reportTableRows,
          margin: { top: imageTop, right: margin, bottom: footerHeight + 7, left: margin },
          styles: {
            font: 'helvetica',
            fontSize: 7.5,
            cellPadding: 1.8,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [226, 232, 240],
            lineWidth: 0.1,
            textColor: [51, 65, 85],
          },
          headStyles: {
            fillColor: [30, 41, 59],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
          },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: {
            0: { cellWidth: 34 },
            1: { cellWidth: 34, halign: 'center' },
            2: { cellWidth: 38, halign: 'center' },
            3: { cellWidth: 74 },
          },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 3) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fontSize = 8.2;
              data.cell.styles.textColor = [15, 23, 42];
            }
          },
          didDrawPage: () => {
            const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pageWidth, headerHeight, 'F');
            if (logoDataUrl) {
              pdf.addImage(logoDataUrl, 'PNG', (pageWidth - 36) / 2, 3.4, 36, 10);
            } else {
              pdf.setTextColor(30, 41, 59);
              pdf.setFont('helvetica', 'bold');
              pdf.setFontSize(18);
              pdf.text('RoomNexa', pageWidth / 2, 8.5, { align: 'center' });
            }
            pdf.setTextColor(30, 41, 59);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            pdf.text('Booking Dashboard Report', pageWidth / 2, 18.6, { align: 'center' });
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(7);
            pdf.setTextColor(100, 116, 139);
            pdf.text(
              `Range: ${rangeLabel} | Generated: ${moment().format('DD MMM YYYY, hh:mm A')} | Page ${currentPage}`,
              pageWidth / 2,
              23.6,
              { align: 'center' }
            );
            pdf.setDrawColor(226, 232, 240);
            pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);
            drawPdfFooter(currentPage);
          },
        });
      }

      pdf.save(filename);
    } finally {
      document.body.classList.remove('dashboard-pdf-exporting');
      setPdfExporting(false);
      exportInProgressRef.current = false;
    }
  };
  
  const metricGrid = getInsightsMetricGridProps();
  const chartPrimaryGrid = getInsightsChartPrimaryGridProps();
  const chartSecondaryGrid = getInsightsChartSecondaryGridProps();
  const gridSpacing = getInsightsGridSpacing();

  return (
    <Box 
      sx={{ 
        p: isCompact ? { xs: 1.25, sm: 2 } : 3,
        backgroundColor: isDark ? '#12151c' : '#f2f4f7',
        minHeight: '100vh',
        minWidth: 0,
        width: '100%',
        transition: 'background-color 0.3s ease',
      }}
    >
      <Box sx={{ mb: isCompact ? 1.25 : 3, width: '100%', minWidth: 0 }}>
        <DashboardHeader
          title="Booking Dashboard"
          forceDark={isCompact}
          showBookingSectionTabs
          navLinks={navLinks}
        />
      </Box>

      <DashboardReportHeader
        title="Booking dashboard insights"
        subtitle="Bookings, cancellations, room bookings and channel analytics in one view."
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
        onRefresh={fetchStatistics}
        onPdfDownload={handleDownloadPdf}
        loading={loading}
        pdfExporting={pdfExporting}
        pdfDisabled={!statistics}
        themeMode={currentTheme}
        accentColor={themeColors.booking}
      />

      {error && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {(loading || statistics) && (
        <Box
          ref={reportRef}
          sx={{
            backgroundColor: currentTheme === 'light' ? '#f2f4f7' : 'transparent',
          }}
        >
          {/* First Row: Total Booking, Cancelled Booking, Active Booking, Total Room Booking */}
          <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
            <Grid item {...metricGrid}>
              <DashboardMetricCard
                variant="performance"
                loading={loading}
                title="Total Booking"
                value={totalBookings}
                unit="bookings"
                data={bookingStatisticsData.map(stat => ({ name: stat.name, value: stat.bookings }))}
                color={themeColors.booking}
                icon={<TrendingUpIcon sx={{ color: themeColors.booking }} />}
                theme={currentTheme}
                formatValue={(val) => val.toString()}
              />
            </Grid>
            <Grid item {...metricGrid}>
              <DashboardMetricCard
                variant="performance"
                loading={loading}
                title="Cancelled Booking"
                value={cancelledBookings}
                unit="bookings"
                data={bookingStatisticsData.map(stat => ({ name: stat.name, value: stat.cancelled }))}
                color={themeColors.cancelled}
                icon={<CancelIcon sx={{ color: themeColors.cancelled }} />}
                theme={currentTheme}
                formatValue={(val) => val.toString()}
              />
            </Grid>
            <Grid item {...metricGrid}>
              <DashboardMetricCard
                variant="performance"
                loading={loading}
                title="Active Booking"
                value={activeBookings}
                unit="bookings"
                data={bookingStatisticsData.map(stat => ({ name: stat.name, value: stat.active }))}
                color={themeColors.active}
                icon={<CheckCircleIcon sx={{ color: themeColors.active }} />}
                theme={currentTheme}
                formatValue={(val) => val.toString()}
              />
            </Grid>
            <Grid item {...metricGrid}>
              <DashboardMetricCard
                variant="performance"
                loading={loading}
                title="Total Room Booking"
                value={totalRoomBookings}
                unit="rooms"
                data={bookingStatisticsData.map(stat => ({ name: stat.name, value: stat.total_room_bookings || 0 }))}
                color={themeColors.rooms}
                icon={<HotelIcon sx={{ color: themeColors.rooms }} />}
                theme={currentTheme}
                formatValue={(val) => val.toString()}
                subtitle={`Active: ${activeRoomBookings} | Cancelled: ${cancelledRoomBookings}`}
              />
            </Grid>
          </Grid>
          {/* Booking Statistics + Sources */}
          <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
            <Grid item {...chartPrimaryGrid}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="BOOKING STATISTICS"
                title=""
                subtitle="Bookings split by status over time"
                data={bookingStatisticsData}
                chartType="bar"
                dataKeys={[
                  { dataKey: 'active', name: 'Active Bookings', color: unifiedChartColors.green, stackId: 'bookingStatus' },
                  { dataKey: 'cancelled', name: 'Cancelled Bookings', color: unifiedChartColors.red, stackId: 'bookingStatus' },
                  { dataKey: 'booking', name: 'Booking', color: unifiedChartColors.yellow, stackId: 'bookingStatus' },
                ]}
                theme={currentTheme}
                height={300}
                formatValue={(val) => val.toString()}
                showBarTotalTooltip
                barTotalLabel="Total Bookings"
                barRadius={[0, 0, 0, 0]}
              />
            </Grid>
            <Grid item {...chartSecondaryGrid}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="BOOKING SOURCES"
                title=""
                subtitle={`Total bookings: ${totalSourceBookings}`}
                chartType="pie"
                pieData={bookingSourceChartData}
                pieAsBars
                pieInnerRadius={70}
                pieShowValueInLabel
                theme={currentTheme}
                height={300}
                formatValue={(val) => Number(val || 0).toString()}
              />
            </Grid>
          </Grid>

          {/* Booking Cancellation by Source */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="BOOKING CANCELLATION BY SOURCE"
                title=""
                subtitle={`Total cancelled bookings: ${totalCancelledSourceBookings || cancelledBookings}`}
                chartType="pie"
                pieData={cancelledBookingSourceChartData}
                pieAsBars
                pieInnerRadius={70}
                pieShowValueInLabel
                theme={currentTheme}
                height={300}
                formatValue={(val) => Number(val || 0).toString()}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="REVENUE LOSS FROM CANCELLED BOOKINGS"
                title=""
                subtitle={`Total loss: ${formatPdfAmount(totalCancelledRevenueLoss)}`}
                chartType="pie"
                pieData={cancelledRevenueLossBySourceData}
                pieAsBars
                pieInnerRadius={70}
                pieShowValueInLabel
                theme={currentTheme}
                height={300}
                formatValue={(val) => formatPdfAmount(val)}
              />
            </Grid>
          </Grid>

          {/* Direct/OTA + Check-in/Checkout */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="DIRECT BOOKINGS VS OTA BOOKINGS"
                title=""
                subtitle={`Total Direct: ${directBookings} | Total OTA: ${otaBookings}`}
                data={channelData}
                chartType="line"
                dataKeys={[
                  { dataKey: 'direct', name: 'Direct Bookings', color: themeColors.booking },
                  { dataKey: 'ota', name: 'OTA Bookings', color: themeColors.revenue },
                ]}
                theme={currentTheme}
                height={300}
                formatValue={(val) => val.toString()}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="EARLY CHECK-IN AND LATE CHECKOUT"
                title=""
                subtitle={`Total Early Check-ins: ${earlyCheckin.total} | Total Late Checkouts: ${lateCheckout.total}`}
                data={checkinCheckoutData}
                chartType="line"
                dataKeys={[
                  { dataKey: 'early_checkin', name: 'Early Check-ins', color: themeColors.active },
                  { dataKey: 'late_checkout', name: 'Late Checkouts', color: themeColors.cancelled },
                ]}
                theme={currentTheme}
                height={300}
                formatValue={(val) => val.toString()}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default BookingDashboardPage;